import { types, Instance, flow, applySnapshot } from "mobx-state-tree";
import { BaseModel } from "../base";
import { automatedRequest, request } from "../../services/cpln";
import { AGENT_CHECKING, agentHelpers } from "./agent.helpers";

export const AgentInfoModel = types
  .model("Agent Info", {
    lastActive: types.optional(types.string, AGENT_CHECKING),
    instanceId: types.optional(types.string, ""),
    protocolVersion: types.optional(types.string, ""),
    env: types.optional(types.frozen({}), () => ({})),
    peerCount: types.optional(types.number, 0),
    serviceCount: types.optional(types.number, 0),
  })
  .actions((self) => ({
    resetLastActive() {
      self.lastActive = "";
    },
  }));

export const AgentModel = types
  .compose(
    "Agent",
    BaseModel,
    types.model({
      status: types.frozen({}),
      infos: types.optional(types.array(AgentInfoModel), []),
      isInfoLoading: types.optional(types.boolean, true),
    })
  )
  .views((self) => ({
    get infosArray() {
      return self.infos.slice();
    },
  }))
  .views((self) => ({
    get envText() {
      return agentHelpers.getEnvText(self as any, self.infosArray);
    },
    get lastActiveText() {
      return agentHelpers.getLastActiveText(self as any, self.infosArray);
    },
    get lastActiveTextTable() {
      return agentHelpers.getLastActiveTextTable(self as any, self.infosArray);
    },
  }))
  .views((self) => ({
    get isActive() {
      return agentHelpers.getIsActive(self as any, self.infosArray);
    },
  }))
  .actions((self) => {
    const fetchInfo: (automated: boolean) => Promise<boolean> = flow(function* (automated: boolean) {
      try {
        self.isInfoLoading = true;
        const url = self.selfLink + "/-info";
        const requestToAwait = automated ? automatedRequest({ url }) : request({ url });
        const { data } = yield requestToAwait;

        // Backwards compatibility: normalize v1 single object to array
        let infosData: any[];
        if (Array.isArray(data)) {
          infosData = data;
        } else if (data && Object.keys(data).length > 0) {
          infosData = [data];
        } else {
          infosData = [];
        }

        applySnapshot(self.infos, infosData);
        self.isInfoLoading = false;
        return true;
      } catch (e) {
        self.isInfoLoading = false;
        if (e.response?.status === 404) {
          return false;
        }
        return true;
      }
    });
    return { fetchInfo };
  })
  .actions((self) => {
    const afterCreate = flow(function* () {
      self.fetchInfo(false);
    });
    const fetch: () => Promise<void> = flow(function* () {
      const { data } = yield request({ url: self.selfLink });
      const currentInfos = self.infos.toJSON();
      applySnapshot(self, { ...data, infos: currentInfos });
      self.fetchInfo(false);
    });
    return { afterCreate, fetch };
  })
  .actions((self) => {
    const patch: (body: Object) => Promise<void> = flow(function* (body: Object) {
      body = Object.assign({}, body, { id: self.id, version: self.version });
      yield request({ method: "patch", url: self.selfLink, body });
      yield self.fetch();
    });
    return { patch };
  });

export interface AgentMobx extends Instance<typeof AgentModel> {}
